import React, { FC, useCallback } from 'react';
import moment from 'moment';
import clsx from 'clsx';
import { DatePicker, FieldWrapper } from '@td-client-frontend/common';
import { Add } from '@td-client-frontend/icons';
import Truncatebale from '../components/Truncatebale';
import ResizableTextArea from '../components/ResizableTextArea';
import { planDateFormatter } from '../../../../utils/plansModalUtils';
import { Plan, PlanError } from '../interfaces/PlanIntrfaces';

interface AddNewPlansProps {
  plan: Plan;
  newPlanErrors: PlanError[];
  deletePlan: (planId: string) => void;
  editFields: (id: string, key: string, editedValue: string) => void;
  showDelete: boolean;
}

const AddNewPlans: FC<AddNewPlansProps> = ({ plan, newPlanErrors, deletePlan, editFields, showDelete }) => {
  const onDelete = useCallback(() => {
    deletePlan(plan.id);
  }, [plan.id, deletePlan]);

  const editCodeHandler = useCallback(
    (value: string) => {
      editFields(plan.id, 'code', value);
    },
    [editFields, plan.id]
  );

  const editDateHandler = useCallback(
    (date: Date, type: string) => {
      const dateValue = planDateFormatter(date);
      editFields(plan.id, type, dateValue);
    },
    [editFields, plan.id]
  );

  const codeError = newPlanErrors?.find((error) => error.id === plan.id && error.type === 'CODE');

  return (
    <div
      className="grid gap-4 border-b-1 border-gray-20 py-2"
      style={{
        gridTemplateColumns: '2fr repeat(2, minmax(0, 1fr)) 0.5fr'
      }}
    >
      <Truncatebale>
        <FieldWrapper
          error={codeError && codeError.reason}
          isError={!!codeError}
          name={`code-${plan.id}`}
          className="flex flex-col w-full"
          fieldLabelClasses="hidden"
          errorClassName={!codeError ? 'hidden' : undefined}
        >
          <ResizableTextArea
            classes={clsx(
              codeError ? 'border-red-500 border-2' : 'border-gray-default border',
              'flex flex-col bg-white p-4 leading-none resize-none rounded-lg appearance-none font-normal font-effra text-black-default focus:outline-none active:outline-none focus:border-2 focus:border-black'
            )}
            value={plan.code || ''}
            name={plan.id}
            onChange={editCodeHandler}
            maxHeight={8}
          />
        </FieldWrapper>
      </Truncatebale>
      <Truncatebale>
        <DatePicker
          value={moment(plan.start_dt).isValid() ? moment(plan.start_dt).format('MM/DD/YYYY') : ''}
          placeholder="MM/DD/YYYY"
          onChange={(date: Date) => editDateHandler(date, 'start_dt')}
          wrapperClasses="flex flex-col w-full"
          fieldLabelClasses="hidden"
          errorClassName={!codeError ? 'hidden' : undefined}
          closeOnScroll
        />
      </Truncatebale>
      <Truncatebale>
        <DatePicker
          value={moment(plan.end_dt).isValid() ? moment(plan.end_dt).format('MM/DD/YYYY') : ''}
          placeholder="MM/DD/YYYY"
          onChange={(date: Date) => editDateHandler(date, 'end_dt')}
          wrapperClasses="flex flex-col w-full"
          fieldLabelClasses="hidden"
          errorClassName={!codeError ? 'hidden' : undefined}
          closeOnScroll
        />
      </Truncatebale>

      {showDelete ? (
        <button
          onClick={onDelete}
          className="flex transform rotate-45 w-6 h-6 mt-4 justify-self-center items-center focus:outline-none active:outline-none hover:transform hover:scale-110"
        >
          <Add />
        </button>
      ) : (
        <div />
      )}
    </div>
  );
};

export default AddNewPlans;
